import { HIGHLIGHTED_PRICE_MAX_ITEMS } from '@/features/dashboard/constants/const';
import type { MarketAppWithTabs, TileBigNumber } from '@/features/dashboard/types';

import type {
  DashboardEditDraft,
  DashboardEditState,
  DraftTab,
  TabDraft
} from '../dashboardEdit.types';

/**
 * Checks if the target dashboard is the current dashboard
 */
export const isCurrentDashboard = (
  state: DashboardEditState,
  targetDashboardName: string
): boolean => {
  return (
    targetDashboardName === state.dashboardName ||
    (!!state.initialDashboardName && targetDashboardName === state.initialDashboardName)
  );
};

/**
 * Ensures a dashboard draft exists and returns it.
 * If the draft doesn't exist but server data is available in allDashboardsData,
 * initializes the draft from that data instead of creating an empty draft.
 */
export const ensureDashboardDraft = (
  state: DashboardEditState,
  dashboardName: string
): { draftTabs: DraftTab[]; tabDrafts: Partial<Record<string, TabDraft>> } => {
  // Return existing draft if available
  const existing = state.otherDashboardDrafts[dashboardName];
  if (existing) {
    return existing;
  }

  // Fallback: create empty draft
  const emptyDraft: DashboardEditDraft = { draftTabs: [], tabDrafts: {} };
  state.otherDashboardDrafts[dashboardName] = emptyDraft;
  return emptyDraft;
};

/**
 * Ensures a tab draft exists within a dashboard draft and returns it
 */
export const ensureTabDraft = (
  dashboardDraft: { draftTabs: DraftTab[]; tabDrafts: Partial<Record<string, TabDraft>> },
  tabId: string
): TabDraft => {
  const existing = dashboardDraft.tabDrafts[tabId];
  if (existing) return existing;
  const newDraft: TabDraft = { marketTabApps: [], tiles: [] };
  dashboardDraft.tabDrafts[tabId] = newDraft;
  return newDraft;
};

/**
 * Marks a dashboard as dirty if not already marked
 */
export const markDashboardDirty = (state: DashboardEditState, dashboardName: string): void => {
  if (!state.dirtyDashboards.includes(dashboardName)) {
    state.dirtyDashboards.push(dashboardName);
  }
};

/**
 * Gets the current tab draft or null if not found
 */
export const getCurrentTabDraft = (state: DashboardEditState): TabDraft | null => {
  if (!state.currentTabId) return null;
  return state.tabDrafts[state.currentTabId] ?? null;
};

/**
 * Stamps a tab's `updatedAt`, given the `draftTabs` array it lives in. Used to
 * roll a content or metadata change up to the tab (and, at read time, the
 * dashboard) level.
 */
export const touchTab = (
  draftTabs: DraftTab[] | undefined,
  tabId: string,
  timestamp: string
): void => {
  const draftTab = draftTabs?.find(t => t.id === tabId);
  if (draftTab) draftTab.updatedAt = timestamp;
};

/**
 * Validation error types for tile operations
 */
export type TileValidationError = 'MAX_TILES_EXCEEDED' | 'DUPLICATE_TILE_CODE' | null;

/**
 * Gets the tile code from a tile
 */
export const getTileCode = (tile: TileBigNumber): string => {
  return tile.symbol.key.Code;
};

/**
 * Checks if a tile with the given code already exists in the tiles array
 */
export const tileCodeExists = (tiles: TileBigNumber[], tileCode: string): boolean => {
  return tiles.some(t => getTileCode(t) === tileCode);
};

/**
 * Validates if a tile can be added to a tab draft
 * Returns null if valid, or an error type if invalid
 */
export const validateTileAddition = (
  tabDraft: TabDraft,
  tile: TileBigNumber
): TileValidationError => {
  // Check max tiles limit
  if (tabDraft.tiles.length >= HIGHLIGHTED_PRICE_MAX_ITEMS) {
    return 'MAX_TILES_EXCEEDED';
  }

  // Check for duplicate tile code
  const tileCode = getTileCode(tile);
  if (tileCodeExists(tabDraft.tiles, tileCode)) {
    return 'DUPLICATE_TILE_CODE';
  }

  return null;
};

/**
 * Gets a user-friendly error message for a tile validation error
 */
export const getTileValidationErrorMessage = (error: TileValidationError): string => {
  switch (error) {
    case 'MAX_TILES_EXCEEDED':
      return `Maximum ${HIGHLIGHTED_PRICE_MAX_ITEMS} tiles per tab allowed`;
    case 'DUPLICATE_TILE_CODE':
      return 'A tile with this code already exists in the tab';
    default:
      return 'Unknown error';
  }
};

/**
 * Checks if an app with the given ID already exists in the apps array
 */
export const appIdExists = (apps: MarketAppWithTabs[], appId: string): boolean => {
  return apps.some(a => a.id === appId);
};

/**
 * Generates a new unique app ID that doesn't conflict with existing apps
 */
export const generateUniqueAppId = (existingApps: MarketAppWithTabs[]): string => {
  let newId: string;
  do {
    newId = crypto.randomUUID();
  } while (appIdExists(existingApps, newId));
  return newId;
};

/**
 * Ensures the app has a unique ID in the target apps array.
 * If the app ID already exists, returns a new app with a generated unique ID.
 * Otherwise returns the original app.
 */
export const ensureUniqueAppId = (
  app: MarketAppWithTabs,
  existingApps: MarketAppWithTabs[]
): MarketAppWithTabs => {
  if (appIdExists(existingApps, app.id)) {
    return {
      ...app,
      id: generateUniqueAppId(existingApps)
    };
  }
  return app;
};

/**
 * Tab validation error type
 */
export type TabValidationError = 'DUPLICATE_TAB_NAME' | null;

/**
 * Checks if a tab with the given name already exists in the tabs array
 * Comparison is case-insensitive
 */
export const tabNameExists = (tabs: DraftTab[], tabName: string): boolean => {
  const normalizedName = tabName.toLowerCase().trim();
  return tabs.some(t => t.name.toLowerCase().trim() === normalizedName);
};

/**
 * Validates if a tab can be moved to a dashboard
 * Returns null if valid, or an error type if invalid
 */
export const validateTabMove = (existingTabs: DraftTab[], tabName: string): TabValidationError => {
  if (tabNameExists(existingTabs, tabName)) {
    return 'DUPLICATE_TAB_NAME';
  }
  return null;
};

/**
 * Gets a user-friendly error message for a tab validation error
 */
export const getTabValidationErrorMessage = (
  error: TabValidationError,
  tabName: string
): string => {
  if (error === 'DUPLICATE_TAB_NAME') {
    return `A tab named "${tabName}" already exists in this dashboard`;
  }
  return 'Unknown error';
};

/**
 * Generates a unique tab name by appending a numeric suffix if needed.
 * Examples: "New Page", "New Page (2)", "New Page (3)", etc.
 *
 * @param existingTabs - Array of existing tabs to check against
 * @param baseName - Base name to use (e.g., "New Page")
 * @returns A unique tab name that doesn't conflict with existing tabs
 */
export const generateUniqueTabName = (existingTabs: DraftTab[], baseName: string): string => {
  // If base name doesn't exist, use it
  if (!tabNameExists(existingTabs, baseName)) {
    return baseName;
  }

  // Try incrementing numbers until we find a unique name
  let counter = 2;
  let candidateName = `${baseName} (${counter})`;

  while (tabNameExists(existingTabs, candidateName)) {
    counter++;
    candidateName = `${baseName} (${counter})`;
  }

  return candidateName;
};
